import { apiClient } from './client'
import type {
  CreateDossierRequest,
  DossierArtifactView,
  DossierComponentPreviewView,
  DossierComponentView,
  DossierJobHandle,
  DossierSummaryView,
  DossierView,
} from '../types/dossier'

/**
 * Typed fetch wrappers for `module.dossier`'s Dossier/DossierComponent surface (IB-159) — same
 * thin-wrapper convention as `api/film.ts`/`api/cases.ts`. `POST /cases/{caseId}/dossiers` is NOT
 * on the platform's `Idempotency-Key`-required allow-list (`IdempotencyFilterConfig` deliberately
 * excludes the whole `/api/v1/cases/*` prefix — deferred to IB-52) — no `idempotencyKey` option
 * here, matching the real backend contract rather than sending a header the filter never checks.
 */

export async function createDossier(caseId: string, request: CreateDossierRequest): Promise<DossierView> {
  const { data } = await apiClient.post<DossierView>(`/cases/${caseId}/dossiers`, request)
  return data
}

/**
 * `GET /cases/{caseId}/dossiers` (closes the disclosed list gap) — every version (draft + prior
 * finals) of every kind, newest-first, own-tenant/visibility-scoped. This is now `DossierPanel`'s
 * source of truth for "does this case have an open draft" instead of the old sessionStorage cache.
 */
export async function listCaseDossiers(caseId: string, signal?: AbortSignal): Promise<DossierSummaryView[]> {
  const { data } = await apiClient.get<DossierSummaryView[]>(`/cases/${caseId}/dossiers`, { signal })
  return data
}

export async function getDossier(id: string, signal?: AbortSignal): Promise<DossierView> {
  const { data } = await apiClient.get<DossierView>(`/dossiers/${id}`, { signal })
  return data
}

export async function listDossierComponents(id: string, signal?: AbortSignal): Promise<DossierComponentView[]> {
  const { data } = await apiClient.get<DossierComponentView[]>(`/dossiers/${id}/components`, { signal })
  return data
}

/**
 * Live, NOT-persisted preview of one component's content (AC2b) — sensitive-read audited
 * server-side. Callers must gate this to `status === 'generated' || status === 'stale'` components
 * (never `pending`/the declared `approved_version` stub) — this wrapper does not enforce that
 * itself, `DossierPanel` does, matching the AC.
 */
export async function previewDossierComponent(
  dossierId: string,
  componentId: string,
): Promise<DossierComponentPreviewView> {
  const { data } = await apiClient.get<DossierComponentPreviewView>(
    `/dossiers/${dossierId}/components/${componentId}/preview`,
  )
  return data
}

/**
 * `POST .../generate` — submits against `JobKind.DOSSIER_ASSEMBLY`, which dispatches inline (v1,
 * ADR-0033(c)): by the time this resolves, `status` is already terminal. Callers must re-fetch
 * `listDossierComponents`/`getDossier` after this resolves — never apply anything optimistically.
 */
export async function generateDossier(id: string): Promise<DossierJobHandle> {
  const { data } = await apiClient.post<DossierJobHandle>(`/dossiers/${id}/generate`, undefined)
  return data
}

/** `POST .../finalize` — same inline-dispatch contract as {@link generateDossier}. */
export async function finalizeDossier(id: string): Promise<DossierJobHandle> {
  const { data } = await apiClient.post<DossierJobHandle>(`/dossiers/${id}/finalize`, undefined)
  return data
}

/** Final-only (AC2d/AC18) — 404s while the Dossier is still `draft`; never returns a draft's bytes. */
export async function getDossierArtifact(id: string): Promise<DossierArtifactView> {
  const { data } = await apiClient.get<DossierArtifactView>(`/dossiers/${id}/artifact`)
  return data
}
